import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import {
  Scroll, Search, ChevronLeft, ChevronRight, BookMarked, Bookmark, Sparkles, MapPin,
  Landmark, ArrowRight, ExternalLink, FileText, Handshake, ScrollText, AlertTriangle, Loader2,
} from "lucide-react";

const field = "w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50";

const SUBJECT_STYLE = "bg-[#F3EEFA] text-[#59288E]";

function AgreedRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 py-2.5 border-b border-[#EFE9E1] last:border-0">
      <Icon size={16} className="text-[#8F7220] shrink-0 mt-0.5" />
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220]">{label}</div>
        <div className="text-sm text-[#1A171D] mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function TreatyDetail({ t, saved, note, onBack, onToggleSave, onSaveNote, onNavigate }) {
  const [explain, setExplain] = useState(null);
  const [loadingExplain, setLoadingExplain] = useState(false);
  const [noteText, setNoteText] = useState(note || "");
  useEffect(() => { setNoteText(note || ""); }, [note]);

  const runExplain = async () => {
    setLoadingExplain(true);
    try { const { data } = await api.post(`/treaties/${t.id}/explain`); setExplain(data.explanation); }
    catch (e) { toast.error("Could not generate explanation. Please try again."); }
    finally { setLoadingExplain(false); }
  };

  const a = t.agreed || {};
  return (
    <div data-testid="treaty-detail">
      <button onClick={onBack} data-testid="treaty-back-btn" className="inline-flex items-center gap-1.5 text-sm text-[#59288E] font-semibold mb-4 hover:underline"><ChevronLeft size={16} /> Back to treaties</button>

      <Panel className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {t.subjects.map((s) => <span key={s} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${SUBJECT_STYLE}`}>{s}</span>)}
            </div>
            <h2 className="font-serif text-3xl font-bold text-[#180D24]">{t.name}</h2>
            <div className="text-sm text-[#8F7220] font-mono mt-1">{t.date} · {t.nations.join(", ")} & {t.other_party}</div>
          </div>
          <button onClick={() => onToggleSave(t)} data-testid="treaty-save-btn"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${saved ? "bg-[#166534] text-white" : "bg-[#D4AF37] text-[#180D24] hover:bg-[#E7C965]"}`}>
            {saved ? <BookMarked size={15} /> : <Bookmark size={15} />} {saved ? "In my treaty library" : "Save to my research"}
          </button>
        </div>

        {/* Header fields */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {[["Date", t.date], ["Nation(s)", t.nations.join(", ")], ["Other Party", t.other_party],
            ["Signed At", t.signed_at], ["Historical Territory", t.territory],
            ["Evidence Status", t.evidence_status]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-[#FBF7FF] border border-[#59288E]/12 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#8F7220]">{k}</div>
              <div className="text-sm font-semibold text-[#2A1442] mt-0.5">{v}</div>
            </div>
          ))}
        </div>

        {/* Plain-language summary */}
        <h3 className="font-serif text-lg font-semibold text-[#59288E] mt-6 mb-1">In plain language</h3>
        <p className="text-[#1A171D] leading-relaxed">{t.summary}</p>

        {/* Claude deeper explanation */}
        <div className="mt-4">
          {!explain && (
            <button onClick={runExplain} disabled={loadingExplain} data-testid="treaty-explain-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#482276] transition-colors disabled:opacity-60">
              {loadingExplain ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} className="text-[#E7C965]" />}
              {loadingExplain ? "Studying the treaty…" : "Explain this in more depth (AI)"}
            </button>
          )}
          {explain && (
            <div className="mt-2 rounded-xl border border-[#59288E]/15 bg-white p-4" data-testid="treaty-explanation">
              <div className="flex items-center gap-2 text-[#59288E] text-xs font-bold uppercase tracking-wide mb-2"><Sparkles size={14} className="text-[#D4AF37]" /> AI educational explanation</div>
              <div className="text-sm text-[#1A171D] leading-relaxed whitespace-pre-line">{explain}</div>
            </div>
          )}
        </div>

        {/* What each side agreed to */}
        <h3 className="font-serif text-lg font-semibold text-[#59288E] mt-6 mb-1">What did each side agree to?</h3>
        <p className="text-xs text-[#756D7E] mb-2">This summarizes the written instrument — it does not infer obligations that do not appear in the treaty.</p>
        <div className="rounded-xl border border-[#E4DCD3] bg-white p-4">
          <AgreedRow icon={MapPin} label="Land & Boundaries" value={a.land} />
          <AgreedRow icon={Landmark} label="Payments / Annuities" value={a.payments} />
          <AgreedRow icon={ArrowRight} label="Movement / Removal" value={a.movement} />
          <AgreedRow icon={Handshake} label="Reserved Rights" value={a.reserved} />
          <AgreedRow icon={FileText} label="Other Obligations" value={a.other} />
          <AgreedRow icon={AlertTriangle} label="Freedmen / Post-Civil War Provisions" value={a.freedmen} />
        </div>

        {/* Related records → Roll Search */}
        <h3 className="font-serif text-lg font-semibold text-[#59288E] mt-6 mb-1">Treaty connections — related records</h3>
        <p className="text-xs text-[#756D7E] mb-2">Treaties did not exist in isolation. Search these record types in the Indian Roll Search.</p>
        <div className="flex flex-wrap gap-2">
          {t.related_records.map((rec, k) => (
            <button key={k} data-testid={`treaty-related-record-${k}`} onClick={() => onNavigate && onNavigate("roll_search", { search: t.nations[0] })}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E7F6EC] text-[#166534] px-3 py-1 text-xs font-semibold hover:bg-[#166534] hover:text-white transition-colors">
              <ScrollText size={13} /> {rec}
            </button>
          ))}
        </div>

        {/* Source center */}
        <div className="mt-6 rounded-xl bg-[#FCF8EE] border border-[#D4AF37]/40 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-[#8F7220] mb-1">Treaty Source Center</div>
          <div className="text-sm text-[#1A171D]"><span className="font-semibold">{t.source.collection}</span>{t.source.naid ? ` · NAID ${t.source.naid}` : ""}</div>
          <a href={t.source.url} target="_blank" rel="noreferrer" data-testid="treaty-source-link"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#59288E] hover:underline">
            <ExternalLink size={14} /> View original document / source
          </a>
        </div>

        {/* Notes (only when saved) */}
        {saved && (
          <div className="mt-6" data-testid="treaty-notes">
            <h3 className="font-serif text-base font-semibold text-[#180D24] mb-1">My research notes</h3>
            <textarea className={field} rows={3} placeholder="Add notes, questions, or ancestor connections you're studying…"
              value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="treaty-note-input" />
            <button onClick={() => onSaveNote(t.id, noteText)} data-testid="treaty-note-save"
              className="mt-2 rounded-full bg-[#2A1442] text-white px-4 py-2 text-sm font-bold hover:bg-[#482276] transition-colors">Save note</button>
          </div>
        )}
      </Panel>
    </div>
  );
}

export default function Treaties({ onNavigate, navPayload }) {
  const [data, setData] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [related, setRelated] = useState([]);
  const [notes, setNotes] = useState({});
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("browse");

  const [q, setQ] = useState("");
  const [nation, setNation] = useState("");
  const [year, setYear] = useState("");
  const [subject, setSubject] = useState("");
  const [party, setParty] = useState("");
  const [nationView, setNationView] = useState("");

  const load = () => api.get("/treaties").then((r) => { setData(r.data); setSavedIds(r.data.saved_ids); });
  useEffect(() => {
    load();
    api.get("/treaties/related").then((r) => setRelated(r.data.nations)).catch(() => {});
    api.get("/treaties/library").then((r) => setNotes(r.data.notes)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data || !navPayload) return;
    if (navPayload.treatyId) { const t = data.treaties.find((x) => x.id === navPayload.treatyId); if (t) setDetail(t); }
    if (navPayload.nation) { setTab("nations"); setNationView(navPayload.nation); }
  }, [data, navPayload]);

  const toggleSave = async (t) => {
    const { data: res } = await api.post(`/treaties/${t.id}/save`);
    setSavedIds((s) => res.saved ? [...s, t.id] : s.filter((x) => x !== t.id));
    toast.success(res.saved ? "Saved to your treaty library" : "Removed from library");
  };
  const saveNote = async (id, note) => {
    await api.post(`/treaties/${id}/notes`, { note });
    setNotes((n) => ({ ...n, [id]: note }));
    toast.success("Note saved");
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.treaties.filter((t) => {
      if (nation && !t.nations.includes(nation)) return false;
      if (year && String(t.year) !== String(year)) return false;
      if (subject && !t.subjects.includes(subject)) return false;
      if (party && t.other_party !== party) return false;
      if (q) {
        const hay = `${t.name} ${t.nations.join(" ")} ${t.signed_at} ${t.territory} ${t.summary}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, nation, year, subject, party, q]);

  if (!data) return <Loader />;
  if (detail) return <TreatyDetail t={detail} saved={savedIds.includes(detail.id)} note={notes[detail.id]}
    onBack={() => setDetail(null)} onToggleSave={toggleSave} onSaveNote={saveNote} onNavigate={onNavigate} />;

  const byId = Object.fromEntries(data.treaties.map((t) => [t.id, t]));
  const savedTreaties = data.treaties.filter((t) => savedIds.includes(t.id));
  const nationsWithTreaties = data.meta.nations.filter((n) => data.treaties.some((t) => t.nations.includes(n)));
  const timeline = nationView ? data.treaties.filter((t) => t.nations.includes(nationView)).sort((a, b) => a.year - b.year) : [];

  const Card = (t) => (
    <button key={t.id} onClick={() => setDetail(t)} data-testid={`treaty-card-${t.id}`}
      className="text-left rounded-2xl border border-[#E4DCD3] bg-white p-5 hover:border-[#D4AF37] hover:shadow-md transition-all">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono font-bold text-[#8F7220]">{t.year}</span>
        {savedIds.includes(t.id) ? <BookMarked size={14} className="text-[#166534]" /> : null}
      </div>
      <h3 className="font-serif text-lg font-semibold text-[#180D24] mt-1">{t.name}</h3>
      <div className="text-xs text-[#8F7220] font-mono mt-0.5">{t.nations.join(", ")}</div>
      <p className="text-sm text-[#756D7E] mt-2 line-clamp-2">{t.summary}</p>
      <div className="flex flex-wrap gap-1 mt-2">{t.subjects.slice(0, 3).map((s) => <span key={s} className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${SUBJECT_STYLE}`}>{s}</span>)}</div>
    </button>
  );

  return (
    <div data-testid="module-treaties">
      <PageHeader eyebrow="Tribal Treaties" title="Tribal Treaties"
        subtitle="Read the agreements. Understand the history. Study treaties historically associated with the Nations appearing in your genealogy research." />

      {/* Connected to my research */}
      {related.length > 0 && (
        <Panel className="p-6 mb-6 border-[#D4AF37]/40 bg-gradient-to-br from-[#FCF8EE] to-white" data-testid="treaties-related-strip">
          <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-[#D4AF37]" /><h3 className="font-serif text-xl font-semibold text-[#180D24]">Treaties connected to my research</h3></div>
          <p className="text-sm text-[#756D7E] mb-4">Nations appearing in your research give you a historically relevant place to begin. The presence of a Nation does <span className="font-semibold">not</span> establish citizenship, treaty rights, or entitlement — it is a starting point for study.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {related.map((r) => (
              <div key={r.nation} className="rounded-xl border border-[#E4DCD3] bg-white p-4">
                <div className="font-serif text-lg font-semibold text-[#2A1442]">{r.nation}</div>
                <div className="text-xs text-[#8F7220] font-mono mt-0.5">{r.treaty_ids.length} related treaties</div>
                <button data-testid={`related-view-${r.nation.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                  onClick={() => { setTab("nations"); setNationView(byId[r.treaty_ids[0]].nations.find((n) => n === r.nation) || byId[r.treaty_ids[0]].nations[0]); }}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#59288E] hover:gap-2.5 transition-all">View related treaties <ArrowRight size={15} /></button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[["browse", "Find a treaty"], ["nations", "Explore by Nation"], ["library", `My Treaty Library (${savedTreaties.length})`]].map(([id, label]) => (
          <button key={id} data-testid={`treaties-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-[#2A1442] text-white" : "bg-white border border-[#E4DCD3] text-[#59288E] hover:border-[#D4AF37]"}`}>{label}</button>
        ))}
      </div>

      {/* Find a treaty */}
      {tab === "browse" && (
        <>
          <Panel className="p-4 mb-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="relative lg:col-span-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756D7E]" />
                <input className={`${field} pl-9`} placeholder="Keyword search — treaty text, place, terms…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="treaty-search-input" />
              </div>
              <select className={field} value={nation} onChange={(e) => setNation(e.target.value)} data-testid="treaty-nation-filter"><option value="">All Nations</option>{data.meta.nations.map((n) => <option key={n} value={n}>{n}</option>)}</select>
              <select className={field} value={year} onChange={(e) => setYear(e.target.value)} data-testid="treaty-year-filter"><option value="">All Years</option>{data.meta.years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
              <select className={field} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="treaty-subject-filter"><option value="">All Subjects</option>{data.meta.subjects.map((s) => <option key={s} value={s}>{s}</option>)}</select>
              <select className={field} value={party} onChange={(e) => setParty(e.target.value)} data-testid="treaty-party-filter"><option value="">All Governments</option>{data.meta.parties.map((p) => <option key={p} value={p}>{p}</option>)}</select>
            </div>
          </Panel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="treaty-grid">
            {filtered.map(Card)}
            {filtered.length === 0 ? <div className="col-span-full text-center py-12 text-[#756D7E]">No treaties match those filters.</div> : null}
          </div>
        </>
      )}

      {/* Explore by Nation */}
      {tab === "nations" && (
        <div data-testid="treaties-nations">
          <div className="flex flex-wrap gap-2 mb-5">
            {nationsWithTreaties.map((n) => (
              <button key={n} data-testid={`nation-chip-${n.replace(/[^a-zA-Z]/g, "").toLowerCase()}`} onClick={() => setNationView(n)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${nationView === n ? "bg-[#59288E] text-white" : "bg-white border border-[#E4DCD3] text-[#59288E] hover:border-[#D4AF37]"}`}>{n}</button>
            ))}
          </div>
          {!nationView ? <div className="text-center py-12 text-[#756D7E]">Select a Nation to see its chronological treaty timeline.</div> : (
            <Panel className="p-6" data-testid="treaty-timeline">
              <h3 className="font-serif text-2xl font-bold text-[#180D24] mb-1">{nationView} Treaty Timeline</h3>
              <p className="text-sm text-[#756D7E] mb-5">Follow the relationship chronologically rather than studying isolated treaties.</p>
              <div className="relative pl-6 border-l-2 border-[#D4AF37]/50">
                {timeline.map((t) => (
                  <div key={t.id} className="relative mb-6 last:mb-0">
                    <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-[#D4AF37] border-2 border-white" />
                    <div className="text-[11px] font-mono font-bold text-[#8F7220]">{t.date}</div>
                    <button onClick={() => setDetail(t)} data-testid={`timeline-treaty-${t.id}`} className="text-left group">
                      <div className="font-serif text-lg font-semibold text-[#2A1442] group-hover:text-[#59288E]">{t.name}</div>
                      <p className="text-sm text-[#756D7E] mt-0.5 max-w-2xl">{t.summary}</p>
                      <span className="text-sm font-semibold text-[#59288E] inline-flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">Study this treaty <ChevronRight size={14} /></span>
                    </button>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* My Treaty Library */}
      {tab === "library" && (
        <div data-testid="treaties-library">
          {savedTreaties.length === 0 ? <div className="text-center py-14 text-[#756D7E]">Your treaty library is empty. Save treaties you're studying to build your research.</div> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{savedTreaties.map(Card)}</div>
          )}
        </div>
      )}
    </div>
  );
}
